
import React, { Dispatch, SetStateAction } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Chip, createStyles, Grid, Icon, IconButton, InputAdornment, makeStyles, TextField, Typography } from '@material-ui/core';

import { RevisionContext } from '../RevisionContext';
import { HomeContext } from '../../../Home/HomeContext';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { theme } from '../../../../theme';
import { KeywordModel } from '../../../../Models/KeywordModel';
import { BatchCorrectionRemove } from '../components/BatchCorrectionRemove';
import CategoryKeywordService from '../../../../Services/CategoryKeywordService';
import { KeywordBatchModel, keywordBatchModelInitial } from '../../../../Models/KeywordBatchModel';
import { EditAction } from '../../../../shared/enum/EditAction';
import { BatchDto } from '../../../../Models/BatchDto';
import { BatchModel } from '../../../../Models/BatchModel';
import { BatchCorrectionContext } from '../BatchCorrectionContext';

const useStyles = makeStyles(() => createStyles({
    chipItem: {
        margin: '4px',
    },
    keywordsComponents: {
        overflow: 'auto',
        maxHeight: '220px'
    },
}));


export function KeywordBatchCorrection() {
    const classes = useStyles();

    const revisionContext = React.useContext(RevisionContext);
    const homeContext = React.useContext(HomeContext);
    const batchCorrectionContext = React.useContext(BatchCorrectionContext);

    const [keywordListAdd, setKeywordListAdd] = React.useState<KeywordModel[]>([]);
    const [keywordAddSelected, setKeywordAddSelected] = React.useState<KeywordModel[]>([]);

    const [keywordListDelete, setKeywordListDelete] = React.useState<BatchDto[]>([]);
    const [keywordDeleteSelected, setKeywordDeleteSelected] = React.useState<BatchModel[]>([]);

    const [searchAddKeyword, setSearchAddKeyword] = React.useState<string>('');
    const [searchDeleteKeyword, setSearchDeleteKeyword] = React.useState<string>('');

    React.useEffect(() => {
        const open = revisionContext.keywordBatchOpenDialog;

        if (open && keywordListAdd.length === 0)
            fetchClientKeywordList();

        if (open && keywordListDelete.length === 0)
            fetchCategoryKeywordList();
    }, [revisionContext.keywordBatchOpenDialog]);

    const fetchClientKeywordList = () => {
        if (revisionContext.filterClassification.clientId) {
            const keywordBatch = keywordBatchModelInitial;
            keywordBatch.categoriesId = revisionContext.ids;

            CategoryKeywordService.getKeywordsByClient(keywordBatch, revisionContext.filterClassification.clientId)
                .then((res: AxiosResponse) => {
                    const data: KeywordModel[] = res.data;
                    const noneElements = data.filter(x => x.description.toLowerCase() === 'nenhum');
                    let otherElements = data.filter(x => x.description.toLowerCase() !== 'nenhum');

                    if (noneElements.length > 0) {
                        otherElements = [...noneElements, ...otherElements];
                    }
                    setKeywordListAdd([...otherElements]);
                })
                .catch((error: AxiosError) => { homeContext.showError('', error); });
        }
    };

    const fetchCategoryKeywordList = () => {
        const keywordBatch = keywordBatchModelInitial;
        keywordBatch.categoriesId = revisionContext.ids;

        CategoryKeywordService.getKeywordBatch(keywordBatch)
            .then((res: AxiosResponse) => { setKeywordListDelete(res.data.keywordsDto); })
            .catch((error: AxiosError) => { homeContext.showError('', error); });
    };

    const handleSaveButton = () => {
        homeContext.setOpenLoadingDialog(true);

        const keywordBatch = { ...keywordBatchModelInitial };
        keywordBatch.categoriesId = revisionContext.ids;

        if (keywordAddSelected && keywordAddSelected.length > 0)
            keywordBatch.keywords = keywordBatch.keywords.concat([...keywordAddSelected]);

        if (keywordDeleteSelected && keywordDeleteSelected.length > 0)
            keywordBatch.keywords = keywordBatch.keywords.concat([...keywordDeleteSelected]);

        if (keywordBatch.keywords && keywordBatch.keywords.length > 0)
            updateKeywordBatch(keywordBatch);

    };

    const updateKeywordBatch = (keywordBatch: KeywordBatchModel) => {
        CategoryKeywordService.updateKeywordBatch(keywordBatch)
            .then(() => {
                handleClearModal();
                homeContext.showSuccess(`As alterações foram realizadas para todas as ${revisionContext.ids.length} categorizações selecionadas com sucesso!`);
                batchCorrectionContext.setCallSearch(true);
            })
            .catch((error: AxiosError) => { homeContext.showError('', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const handleClearModal = () => {
        revisionContext.setKeywordBatchOpenDialog(false);
        setKeywordListAdd([]);
        setKeywordAddSelected([]);
        setKeywordListDelete([]);
        setKeywordDeleteSelected([]);
    };

    const sortKeywords = (keywords: KeywordModel[]) =>
        [...keywords].sort((a, b) => ('' + a.description).localeCompare(b.description));

    const bringNoneElementsToFirstOrder = (keywords: KeywordModel[]) => {
        const noneElements = keywords.filter(x => x.description.toLowerCase() === 'nenhum');
        const otherElements = keywords.filter(x => x.description.toLowerCase() !== 'nenhum');
        return [...noneElements, ...otherElements];
    };

    const defineKeywordsOrdering = (keywords: KeywordModel[]) => {
        keywords = sortKeywords(keywords);
        keywords = bringNoneElementsToFirstOrder(keywords);
        return keywords;
    };

    const getModal = () => {
        return (<Grid container spacing={2}>
            {getKeywordAdd()}
            {getKeywordRemoveTags()}
        </Grid>
        );
    };

    const getKeywordAdd = () => {
        return (
            <>
                <Grid item xs={12}>
                    <Typography variant="subtitle1"> Palavras-chave/tags</Typography>
                </Grid>
                {getSearchTextField(searchAddKeyword, setSearchAddKeyword)}

                <Grid item xs={12} className={classes.keywordsComponents}>
                    {getKeywordSelect()}
                    {getKeywordNotSelected()}
                </Grid>
            </>
        );
    };

    const getSearchTextField = (searchKeyword: string, setSearchKeyword: Dispatch<SetStateAction<string>>) => {
        return (
            <Grid item xs={12} sm={6}>
                <TextField
                    id='search-key-word'
                    value={searchKeyword}
                    label='Pesquisar'
                    onChange={(event) => { setSearchKeyword(event.target.value); }}
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start">
                            <Icon className={'material-icons-round'}>search</Icon>
                        </InputAdornment>,
                        endAdornment: getEndAdormentKeyword(searchKeyword, setSearchKeyword)
                    }}
                />
            </Grid>
        );
    };

    const getEndAdormentKeyword = (searchKeyword: string, setSearchKeyword: Dispatch<SetStateAction<string>>) => {
        if (searchKeyword.length > 0) {
            return (
                <InputAdornment position="end">
                    <IconButton onClick={() => setSearchKeyword('')}>
                        <Icon className={'material-icons-round'}>close</Icon>
                    </IconButton>
                </InputAdornment>
            );
        }
    };

    const getKeywordSelect = () => {
        return (
            <Grid item xs={12}>
                {keywordAddSelected && keywordAddSelected.map((keyword) => {
                    return <Chip
                        key={keyword.id}
                        label={keyword.description}
                        className={classes.chipItem}
                        deleteIcon={<Icon>close</Icon>}
                        onDelete={() => {
                            handleSelectToList(keyword);
                        }}
                        onClick={() => {
                            handleSelectToList(keyword);
                        }}
                        color='primary'
                    />;
                })}
            </Grid>
        );
    };

    const handleSelectToList = (keyword: KeywordModel) => {
        const listKeywordSelect = JSON.parse(JSON.stringify(keywordAddSelected));
        const index = listKeywordSelect.findIndex((k: KeywordModel) => k.id == keyword.id);

        listKeywordSelect.splice(index, 1);

        let listKeywordNotSelect = JSON.parse(JSON.stringify(keywordListAdd));

        keyword.editAction = EditAction.NONE;

        listKeywordNotSelect.push(keyword);
        listKeywordNotSelect = defineKeywordsOrdering(listKeywordNotSelect);

        setKeywordAddSelected(listKeywordSelect);
        setKeywordListAdd(listKeywordNotSelect);
    };

    const getKeywordNotSelected = () => {
        const listFilter = (searchAddKeyword != '') ?
            keywordListAdd.reduce(function (filtered: KeywordModel[], keyword) {
                if (keyword.description.toLowerCase().includes(searchAddKeyword.toLowerCase()))
                    filtered.push(keyword);

                return filtered;
            }, []) : keywordListAdd;

        return (
            <Grid item xs={12}>
                {listFilter && listFilter.map((keyword) => {
                    return <Chip
                        key={keyword.id}
                        label={keyword.description}
                        className={classes.chipItem}
                        onClick={() => {
                            const listKeywordSelect = JSON.parse(JSON.stringify(keywordAddSelected));

                            keyword.editAction = EditAction.INSERT;

                            listKeywordSelect.push(keyword);

                            const listKeywordNotSelect = JSON.parse(JSON.stringify(keywordListAdd));
                            const index = listKeywordNotSelect.findIndex((k: KeywordModel) => k.id == keyword.id);
                            listKeywordNotSelect.splice(index, 1);

                            setKeywordAddSelected(listKeywordSelect);
                            setKeywordListAdd(listKeywordNotSelect);
                        }}
                        color='primary'
                        variant="outlined"
                    />;
                })}
            </Grid>
        );
    };

    const getKeywordRemoveTags = () => {
        return (
            <>
                <Grid item xs={12}>
                    <Typography variant='subtitle1'>Remover palavras-chave/tags</Typography>
                    <Typography variant='caption'>*As palavras-chave sem preenchimento não são comuns às categorizações selecionadas</Typography>
                </Grid>
                {getSearchTextField(searchDeleteKeyword, setSearchDeleteKeyword)}
                <Grid item xs={12}>
                    <BatchCorrectionRemove
                        list={keywordListDelete}
                        setList={setKeywordListDelete}
                        removed={keywordDeleteSelected}
                        setRemoved={setKeywordDeleteSelected}
                        searchText={searchDeleteKeyword} />
                </Grid>
            </>
        );
    };

    return (
        <BasicConfirmDialog
            title={'Alteração de palavras-chave'}
            description={'Exclua ou adicione palavras chaves'}
            handleFirstButton={() => handleClearModal()}
            handleSecondButton={() => handleSaveButton()}
            textFirstButton='DESCARTAR'
            textSecondButton='SALVAR'
            open={revisionContext.keywordBatchOpenDialog}
            children={getModal()}
            cssProperties={{
                minWidth: '800px',
                maxWidth: '800px',
                padding: '1rem',
                colorFirstButton: theme.palette.primary.main,
                justifyButtons: 'flex-end',
                titleVariant: 'h4',
                descriptionVariant: 'body1'
            }}

        />
    );
}